
import { Component, Ref, Vue } from 'vue-property-decorator'
import ManageNoticeList from '@/components/templates/manage/ManageNoticeList.vue'
import { NotificationType } from '@/const/NotificationType'
import { NoticeMessageData } from '@/models/NoticeMessage'
import { SelectItem } from '@/models/SelectItem'
import { NotificationsApi } from '@/api/methods/manage/NotificationsApi'
import NoticeUtils from '@/utils/NoticeUtils'

/**
 * お知らせ一覧画面
 */
@Component({
  components: {
    ManageNoticeList,
  },
})
export default class AcademyNoticeList extends Vue {
  /** お知らせ一覧 */
  @Ref()
  manageNoticeList!: ManageNoticeList

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 10

  /** ページ指定の初期値 */
  private readonly DEFAULT_PAGE_NUMBER = 1

  /** 現在日時 */
  private nowDate = new Date()

  /** お知らせ一覧 */
  private messageList: NoticeMessageData[] = []

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ */
  private currentPage = 1

  /** ソート順(初期は降順=true) */
  private sortOrder = true

  /** ソートインジケータの反転 */
  private sortIndicator = false

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // ページ番号設定
    this.currentPage = Number(this.queryPageNumber) || this.DEFAULT_PAGE_NUMBER

    // お知らせ一覧取得
    await this.getNotifications(
      Number(this.defaultYearCode),
      Number(this.defaultMonthCode),
      this.defaultNotificationTypeCode
    )

    // クエリが存在しない場合、クエリパラメータを更新
    if (Object.keys(this.$route.query).length === 0) {
      await this.replaceQueryParams()
    }

    this.$loading.complete()
  }

  /**
   * お知らせタイプの初期値
   *
   * @returns お知らせタイプコード
   */
  private get defaultNotificationTypeCode(): string {
    let notificationTypeCode = this.$route.query.notificationType

    if (!notificationTypeCode || typeof notificationTypeCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      notificationTypeCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const notificationType = NotificationType.findByCode(notificationTypeCode)

    return notificationType?.code || NotificationType.ALL.code
  }

  /**
   * お知らせタイプのリスト
   *
   * @returns SelectItem型のリスト
   */
  private get notificationTypeList(): SelectItem[] {
    const list: SelectItem[] = []
    NotificationType.LIST.forEach((item) => {
      list.push({ value: item.code, label: item.label })
    })
    return list
  }

  /**
   * 年の初期値
   *
   * @returns 年コード
   */
  private get defaultYearCode(): string {
    let yearCode = this.$route.query.year

    if (!yearCode || typeof yearCode !== 'string') {
      // 未指定時、指定不正時は現在の年を返す
      return this.nowDate.getFullYear().toString()
    }

    // 有効な範囲かをチェック
    if (NoticeUtils.yearList().includes(Number(yearCode))) {
      return yearCode
    } else {
      // 無効だった場合、現在の年を返す
      return this.nowDate.getFullYear().toString()
    }
  }

  /**
   * 年のリスト
   *
   * @returns 年のリスト
   */
  private get selectYearList(): SelectItem[] {
    return NoticeUtils.yearList().map((year) => {
      return {
        value: year.toString(),
        label: year.toString(),
      }
    })
  }

  /**
   * 月の初期値
   *
   * @returns 月コード
   */
  private get defaultMonthCode(): string {
    let monthCode = this.$route.query.month

    if (!monthCode || typeof monthCode !== 'string') {
      // 未指定時、指定不正時は現在の月を返す
      return this.datePadStart((this.nowDate.getMonth() + 1).toString())
    }

    // 有効な範囲か確認
    if (Number(monthCode) >= 1 && Number(monthCode) <= 12) {
      return monthCode
    }

    // 無効な値だった場合、現在の月を返す
    return this.datePadStart((this.nowDate.getMonth() + 1).toString())
  }

  /**
   * 月のリスト
   *
   * @returns 月のリスト
   */
  private get selectMonthList(): SelectItem[] {
    const list = []
    for (let i = 1; i <= 12; i++) {
      list.push({
        value: this.datePadStart(i.toString()),
        label: this.datePadStart(i.toString()),
      })
    }
    return list
  }

  /**
   * クエリパラメータから取得したページ番号
   *
   * @returns ページ番号
   */
  private get queryPageNumber(): string | undefined {
    const pageNumber = this.$route.query.pageNumber

    if (!pageNumber || typeof pageNumber !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return pageNumber
  }

  /**
   * クエリパラメータの更新
   */
  private async replaceQueryParams(): Promise<void> {
    await this.$router.replace({
      query: {
        year: String(this.currentYear()),
        month: this.datePadStart(this.currentMonth().toString()),
        notificationType: this.currentNotificationTypeCode(),
        pageNumber: String(this.currentPage),
      },
    })
  }

  /**
   * メッセージ一覧のソート順を更新する
   */
  private async toggleSortOrder(): Promise<void> {
    this.$loading.start()

    this.sortOrder = !this.sortOrder
    this.sortIndicator = !this.sortIndicator

    // お知らせ再取得
    await this.getNotifications(this.currentYear(), this.currentMonth(), this.currentNotificationTypeCode())

    this.$loading.complete()
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    this.currentPage = value

    // お知らせ再取得
    await this.getNotifications(this.currentYear(), this.currentMonth(), this.currentNotificationTypeCode())

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * プルダウンフィルター変更時の処理
   */
  private async changeFilter(): Promise<void> {
    this.$loading.start()

    this.currentPage = 1

    // お知らせ再取得
    await this.getNotifications(this.currentYear(), this.currentMonth(), this.currentNotificationTypeCode())

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 選択中の年
   *
   * @returns 年(数値)
   */
  private currentYear(): number {
    // undefinedケース 発生しないがブロックのため記述
    if (!this.manageNoticeList) {
      return Number(this.defaultYearCode)
    }
    return Number(this.manageNoticeList.currentYearCode())
  }

  /**
   * 選択中の月
   *
   * @returns 月(数値)
   */
  private currentMonth(): number {
    // undefinedケース 発生しないがブロックのため記述
    if (!this.manageNoticeList) {
      return Number(this.defaultMonthCode)
    }
    return Number(this.manageNoticeList.currentMonthCode())
  }

  /**
   * 選択中のお知らせタイプ
   *
   * @returns お知らせタイプのコード
   */
  private currentNotificationTypeCode(): string {
    // undefinedケース 発生しないがブロックのため記述
    if (!this.manageNoticeList) {
      return this.defaultNotificationTypeCode
    }
    return this.manageNoticeList.currentNotificationTypeCode()
  }

  /**
   * 日付を2桁表示にする
   *
   * @param value 日付の文字列
   * @returns 二桁の日付
   */
  private datePadStart(value: string): string {
    return value.padStart(2, '0')
  }

  /**
   * 行クリック時の処理
   *
   * @param messageId メッセージのID
   */
  private eventClickElement(messageId: number): void {
    this.$router.push({
      path: `/academy/notice/${messageId}`,
      query: this.$prevPage.generateQuery(),
    })
  }

  /**
   * お知らせ登録時の処理
   */
  private eventClickRegist(): void {
    this.$router.push('/academy/notice/regist')
  }

  /**
   * お知らせ一覧を再取得する
   *
   * @param year 対象年
   * @param month 対象月
   * @param type お知らせタイプ
   */
  private async getNotifications(year: number, month: number, type: string): Promise<void> {
    const searchResult = await NotificationsApi.searchNotifications(year, month, this.currentPage, this.MAX_PER_PAGE, {
      orderReverse: !this.sortOrder,
      type: type,
    })

    // お知らせ件数からページ数を設定
    const divideNumber = Math.floor(searchResult.totalCount / this.MAX_PER_PAGE)
    this.pageLength = searchResult.totalCount % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // お知らせ情報を設定
    this.messageList = searchResult.notifications.map((noticeDetail) => {
      return {
        id: noticeDetail.id,
        type: noticeDetail.type,
        title: noticeDetail.title,
        startedDate: new Date(noticeDetail.startedAt * 1000),
        endedDate: new Date(noticeDetail.endedAt * 1000),
        description: noticeDetail.message,
        updatedUser: noticeDetail.updatedBy,
        updatedAt: new Date(noticeDetail.updatedAt * 1000),
      }
    })
  }
}
